import { useCallback, useEffect } from "react";

import { useHistory } from "react-router-dom";
import { signInWithCustomToken as firebaseSignInWithCustomToken } from "firebase/auth";

import { consoleErrorWithSentry } from "../../utils";
import { getCookie, setCookie } from "../../utils/cookie";
import { auth } from "../../utils/firebase";
import mixpanel from "../../utils/mixpanel";
import { SegmentEvents, segmentTrack } from "../../utils/segment";
import { useQuery } from "../../utils/useQuery";
import { paths } from "./helpers";

const ERROR_COOKIE_NAME = "login_error";

const pollForCurrentUser = async () => {
  let intervalId: NodeJS.Timeout;
  const startTime = Date.now(); // Current time in milliseconds

  if (auth.currentUser) {
    return true;
  }

  return new Promise<boolean>((resolve) => {
    intervalId = setInterval(() => {
      const timeElapsed = Date.now() - startTime;

      if (auth.currentUser) {
        clearInterval(intervalId);
        resolve(true);
      }

      if (timeElapsed >= 15000) {
        // 15 seconds have passed
        clearInterval(intervalId);
        resolve(false);
      }
    }, 500); // Poll every 500 milliseconds
  });
};

export const LoginSuccess = () => {
  const history = useHistory();
  const query = useQuery();

  const errHandler = useCallback(
    (err: Error) => {
      consoleErrorWithSentry(err, { fbAppName: auth.app.name });
      setCookie(`${ERROR_COOKIE_NAME}=${err.message}`);
      history.push(paths.loginError);
    },
    [history]
  );

  const signInWithCustomToken = useCallback(async () => {
    const mode = query.get("mode");
    if (mode === "trial") {
      segmentTrack(SegmentEvents.SignUp, null);
    } else if (mode === "flexsave") {
      mixpanel.track("flexsaveSA.signup");
    }

    try {
      const token = getCookie("token");
      if (!token) {
        if (!(await pollForCurrentUser())) {
          errHandler(new Error("no token and no current user"));
          return;
        }
        return; // already logged in - do nothing
      }

      const tenantId = getCookie("tenant_id");
      if (tenantId) {
        auth.tenantId = tenantId;
      } else {
        errHandler(new Error("no tenant_id cookie"));
        return;
      }

      await firebaseSignInWithCustomToken(auth, token);
    } catch (err: any) {
      errHandler(err);
    }
  }, [query, errHandler]);

  useEffect(() => {
    signInWithCustomToken();
  }, [signInWithCustomToken]);

  return <></>;
};
